import { stripFences } from './code.ts';
import { EXAMPLES } from './examples.ts';
import { parseStreamEvent, StreamSequence } from './stream.ts';
import type { FinalReport, StreamEvent } from './stream.ts';

/**
 * A genuine /api/generate run captured by scripts/record-replay.mjs.
 *
 * `events` are the SSE `data:` payloads exactly as they arrived, each with `t`,
 * its arrival time in milliseconds after the request was sent. `model`,
 * `stop_reason` and `usage` repeat what the stream's final event reported.
 */
export interface Recording {
  version: 1;
  prompt: string;
  chip: string | null;
  model: string;
  recordedAt: string;
  events: RecordedEvent[];
  stop_reason: string | null;
  usage: { output_tokens: number };
}

export interface RecordedEvent {
  t: number;
  data: string;
}

/** A validated recording, parsed once so playback feeds the same events the live stream would. */
export interface Replay {
  id: string;
  file: string;
  prompt: string;
  chip: string | null;
  model: string;
  recordedAt: string;
  final: FinalReport;
  events: ReadonlyArray<{ t: number; event: StreamEvent }>;
  code: string;
}

export class RecordingError extends Error {
  constructor(file: string, problem: string) {
    super(`Invalid replay recording ${file}: ${problem}`);
    this.name = 'RecordingError';
  }
}

const RECORDING_KEYS = ['version', 'prompt', 'chip', 'model', 'recordedAt', 'events', 'stop_reason', 'usage'] as const;
const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): string | null {
  const extra = Object.keys(value).filter((k) => !keys.includes(k));
  const missing = keys.filter((k) => !Object.hasOwn(value, k));
  if (extra.length === 0 && missing.length === 0) return null;
  return [missing.length > 0 ? `missing ${missing.join(', ')}` : '', extra.length > 0 ? `unexpected ${extra.join(', ')}` : '']
    .filter((s) => s !== '')
    .join('; ');
}

/** The id a recording file is known by: its file name without directory or extension. */
export function recordingId(file: string): string {
  const name = file.split('/').pop() ?? file;
  return name.replace(/\.json$/, '');
}

/** Validates one parsed recording file. Every deviation from the format throws a RecordingError. */
export function validateRecording(value: unknown, file: string): Replay {
  function fail(problem: string): never {
    throw new RecordingError(file, problem);
  }

  if (!isRecord(value)) fail('the file is not a JSON object');
  const keyProblem = exactKeys(value, RECORDING_KEYS);
  if (keyProblem !== null) fail(`top-level keys: ${keyProblem}`);

  const { version, prompt, chip, model, recordedAt, events, stop_reason, usage } = value;
  if (version !== 1) fail(`version must be 1, got ${JSON.stringify(version)}`);
  if (typeof prompt !== 'string' || prompt.trim() === '' || prompt !== prompt.trim()) {
    fail('prompt must be a non-empty, trimmed string');
  }
  if (chip !== null && typeof chip !== 'string') fail('chip must be null or a chip id');
  if (typeof chip === 'string') {
    const example = EXAMPLES.find((e) => e.id === chip);
    if (example === undefined) fail(`chip "${chip}" is not one of ${EXAMPLES.map((e) => e.id).join(', ')}`);
    else if (example.prompt !== prompt) fail(`chip "${chip}" has the prompt "${example.prompt}", but the recording was made with "${prompt}"`);
  }
  if (typeof model !== 'string' || model === '') fail('model must be a non-empty string');
  if (typeof recordedAt !== 'string' || !ISO_UTC.test(recordedAt) || new Date(recordedAt).toISOString() !== recordedAt) {
    fail('recordedAt must be an ISO 8601 UTC timestamp such as 2026-09-29T14:03:11.402Z');
  }
  if (stop_reason !== null && (typeof stop_reason !== 'string' || stop_reason === '')) {
    fail('stop_reason must be null or a non-empty string');
  }
  if (!isRecord(usage)) fail('usage must be an object');
  const usageProblem = exactKeys(usage, ['output_tokens']);
  if (usageProblem !== null) fail(`usage keys: ${usageProblem}`);
  const outputTokens = usage.output_tokens;
  if (typeof outputTokens !== 'number' || !Number.isInteger(outputTokens) || outputTokens < 0) {
    fail('usage.output_tokens must be a non-negative integer');
  }
  if (!Array.isArray(events) || events.length === 0) fail('events must be a non-empty array');

  const sequence = new StreamSequence();
  let previous = 0;
  const parsed = events.map((entry: unknown, i) => {
    const at = `events[${i}]`;
    if (!isRecord(entry)) fail(`${at} is not an object`);
    const entryProblem = exactKeys(entry, ['t', 'data']);
    if (entryProblem !== null) fail(`${at} keys: ${entryProblem}`);
    const { t, data } = entry;
    if (typeof t !== 'number' || !Number.isFinite(t) || t < 0) fail(`${at}.t must be a non-negative number of milliseconds`);
    if (t < previous) fail(`${at}.t (${t}) is earlier than the event before it (${previous})`);
    previous = t;
    if (typeof data !== 'string') fail(`${at}.data must be the raw SSE data string`);
    let event: StreamEvent;
    try {
      event = parseStreamEvent(data);
      sequence.accept(event);
    } catch (e) {
      fail(`${at}: ${e instanceof Error ? e.message : String(e)}`);
    }
    if (event.kind === 'error') fail(`${at} is an error event; a recording must be a completed stream`);
    return { t, event };
  });

  if (!sequence.ended) fail('events do not end with [DONE]');
  const finals = parsed.flatMap(({ event }) => (event.kind === 'final' ? [event.report] : []));
  const final = finals[0];
  if (final === undefined) fail('events contain no final event');
  if (final.model !== model) fail(`model "${model}" differs from the final event's "${final.model}"`);
  if (final.stop_reason !== stop_reason) fail(`stop_reason ${JSON.stringify(stop_reason)} differs from the final event's ${JSON.stringify(final.stop_reason)}`);
  if (final.output_tokens !== outputTokens) fail(`usage.output_tokens ${outputTokens} differs from the final event's ${final.output_tokens}`);

  const raw = parsed.map(({ event }) => (event.kind === 'text' ? event.text : '')).join('');
  const code = stripFences(raw);
  if (code.trim() === '') fail('the recorded stream contains no code');

  return {
    id: recordingId(file),
    file,
    prompt,
    chip,
    model,
    recordedAt,
    final,
    events: parsed,
    code,
  };
}

/** Parses the text of one recording file; a syntax error names the file. */
export function parseRecordingFile(file: string, text: string): unknown {
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new RecordingError(file, `not valid JSON: ${e instanceof Error ? e.message : String(e)}`);
  }
}

/**
 * Validates a whole set of recordings: every file on its own, then the set.
 * Each chip may have one recording. At most one recording has no chip; it is the
 * opening sample, so it must have finished with stop_reason `end_turn`.
 */
export function validateRecordings(files: ReadonlyArray<readonly [file: string, value: unknown]>): Replay[] {
  const replays = [...files].sort(([a], [b]) => a.localeCompare(b)).map(([file, value]) => validateRecording(value, file));
  replays.forEach((replay, i) => {
    const twin = replays.slice(0, i).find((r) => r.chip === replay.chip);
    if (twin !== undefined) {
      throw new RecordingError(
        replay.file,
        replay.chip === null
          ? `${twin.file} is already the opening recording (chip null), and only one recording may have no chip`
          : `chip "${replay.chip}" already has a recording in ${twin.file}`,
      );
    }
    if (replay.chip === null && replay.final.stop_reason !== 'end_turn') {
      throw new RecordingError(
        replay.file,
        `a recording without a chip is the opening sample and must have finished with stop_reason "end_turn", not ${JSON.stringify(replay.final.stop_reason)}`,
      );
    }
  });
  return replays;
}

/** The recording the page opens on: the one without a chip, otherwise the first chip, in chip order, whose run finished with end_turn. */
export function openingReplay(replays: readonly Replay[]): Replay | null {
  return (
    replays.find((r) => r.chip === null) ??
    EXAMPLES.map((e) => replays.find((r) => r.chip === e.id)).find((r) => r !== undefined && r.final.stop_reason === 'end_turn') ??
    null
  );
}
